import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Linkedin, Shield, CheckCircle2, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// Custom X (Twitter) icon component
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

interface FooterSectionProps {
  title: string;
  links: { label: string; href: string; isExternal?: boolean }[];
  isLegal?: boolean;
}

function FooterSection({ title, links, isLegal }: FooterSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="border-b border-slate-800/50 md:border-none">
      <button
        onClick={() => isMobile && setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-5 text-left md:hidden"
      >
        <span className="text-xs font-bold text-slate-200 tracking-widest uppercase">{title}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <ChevronDown className="h-4 w-4 text-slate-500" />
        </motion.div>
      </button>

      <h3 className="hidden md:block text-[11px] font-black text-slate-400 tracking-[0.2em] uppercase mb-8">
        {title}
      </h3>

      <AnimatePresence initial={false}>
        {(isOpen || !isMobile) && (
          <motion.ul
            initial={isMobile ? { height: 0, opacity: 0 } : false}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden pb-6 md:pb-0 space-y-4"
          >
            {links.map((link) => (
              <li key={link.href}>
                {link.isExternal ? (
                  <a
                    href={link.href}
                    className="text-[15px] text-slate-400 hover:text-teal-400 hover:translate-x-1 inline-flex items-center transition-all duration-300"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.href}
                    className="text-[15px] text-slate-400 hover:text-teal-400 hover:translate-x-1 inline-flex items-center transition-all duration-300"
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
            {isLegal && (
              <li className="pt-2">
                <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest opacity-60 bg-slate-800/30 px-2 py-1 rounded">
                  RA 10173 Compliance
                </span>
              </li>
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Footer() {
  const { t } = useLanguage();

  const productLinks = [
    { label: t('footer_verify_record'), href: '/verify' },
    { label: t('footer_pricing'), href: '/pricing' },
    { label: t('footer_dashboard'), href: '/dashboard' },
    { label: t('footer_faq'), href: '/faq' },
  ];

  const companyLinks = [
    { label: t('footer_about'), href: '/about' },
    { label: t('footer_overview'), href: '/overview' },
    { label: t('footer_security'), href: '/security' },
    { label: t('footer_contact'), href: '/contact' },
  ];

  const legalLinks = [
    { label: t('footer_privacy'), href: '/privacy-policy' },
    { label: t('footer_terms'), href: '/terms-of-service' },
  ];

  return (
    <footer className="relative z-10 bg-[#070b14] border-t border-slate-800/40 overflow-hidden">
      {/* Premium accent line */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-teal-500/30 to-transparent" />

      {/* Radial glow background */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-teal-600/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto pt-16 md:pt-24 pb-12 px-6 sm:px-10 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-24">
          {/* Brand Column */}
          <div className="md:col-span-5 lg:col-span-4">
            <Link to="/" className="inline-flex items-center group mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-teal-500/20 blur-xl group-hover:bg-teal-500/40 transition-all duration-700" />
                <img
                  src="/pwa-192x192.png"
                  alt="Agos"
                  className="relative h-11 w-11 object-contain group-hover:scale-110 transition-transform duration-700 ease-out"
                />
              </div>
              <span className="ml-4 text-2xl font-black text-white tracking-tight">Agos Identity</span>
            </Link>

            <p className="text-[16px] text-slate-400 leading-relaxed max-w-sm mb-10">
              {t('footer_brand_desc')}
            </p>

            <div className="flex flex-wrap gap-4">
              <div className="group flex items-center gap-2.5 px-4 py-2 rounded-xl bg-emerald-500/5 border border-emerald-500/10 text-emerald-400/80 hover:bg-emerald-500/10 hover:border-emerald-500/20 transition-all duration-300">
                <Shield className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="text-[11px] font-black uppercase tracking-[0.1em]">{t('footer_npc_badge')}</span>
              </div>
              <div className="group flex items-center gap-2.5 px-4 py-2 rounded-xl bg-teal-500/5 border border-teal-500/10 text-teal-400/80 hover:bg-teal-500/10 hover:border-teal-500/20 transition-all duration-300">
                <CheckCircle2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="text-[11px] font-black uppercase tracking-[0.1em]">{t('footer_bsp_badge')}</span>
              </div>
            </div>

            {/* NPC Compliance & Data Residency Notice */}
            <p className="mt-6 text-[10px] text-slate-500 font-bold uppercase tracking-[0.15em] leading-relaxed text-center md:text-left">
              {t('footer_npc_notice')}
            </p>
            <p className="mt-2 text-[10px] text-slate-600 font-black uppercase tracking-widest text-center md:text-left">
              {t('footer_bsp_notice')}
            </p>
          </div>

          {/* Links Grid */}
          <div className="md:col-span-7 lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-8">
              <FooterSection title={t('footer_product')} links={productLinks} />
              <FooterSection title={t('footer_company')} links={companyLinks} />
              <FooterSection title={t('footer_legal')} links={legalLinks} isLegal />
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="mt-20 mb-10 h-px w-full bg-gradient-to-r from-transparent via-slate-800/50 to-transparent" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="text-center md:text-left order-2 md:order-1">
            <p className="text-sm text-slate-400 font-semibold tracking-wide">
              © {new Date().getFullYear()} Agos Identity Solutions.
            </p>
            <p className="text-[11px] text-slate-600 mt-2 font-black uppercase tracking-[0.2em] flex items-center justify-center md:justify-start gap-3">
              <span>Kidapawan City, PH</span>
              <span className="w-1 h-1 rounded-full bg-slate-800" />
              <span>Core v1.0.6-stable</span>
            </p>
          </div>

          <div className="flex items-center space-x-2 order-1 md:order-2">
            {[
              { icon: XIcon, href: 'https://twitter.com/AgosIdentity', label: 'X' },
              { icon: Facebook, href: 'https://facebook.com/AgosIdentity', label: 'Facebook' },
              { icon: Linkedin, href: 'https://linkedin.com/company/agos-identity', label: 'LinkedIn' }
            ].map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-3 text-slate-500 hover:text-white transition-all duration-500"
                aria-label={social.label}
              >
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 rounded-2xl transition-all duration-500 scale-75 group-hover:scale-100" />
                <social.icon className="relative h-5.5 w-5.5 transform group-hover:scale-110 transition-transform duration-500" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}